/**
 * OCGcore duel-message and field-query constants.
 *
 * Source of truth: `ocgcore/ocgapi_constants.h` at the pinned core tag (edo9300/ygopro-core v11.0).
 * Values below are the long-stable ygopro/EDOPro constants. VERIFY against the pinned header before
 * relying on binary payload layouts — see packages/protocol/README.md.
 */
/** Events the core emits via OCG_DuelGetMessage (the duel-event stream). */
export const MSG = {
    RETRY: 1,
    HINT: 2,
    WAITING: 3,
    START: 4,
    WIN: 5,
    UPDATE_DATA: 6,
    UPDATE_CARD: 7,
    REQUEST_DECK: 8,
    SELECT_BATTLECMD: 10,
    SELECT_IDLECMD: 11,
    SELECT_EFFECTYN: 12,
    SELECT_YESNO: 13,
    SELECT_OPTION: 14,
    SELECT_CARD: 15,
    SELECT_CHAIN: 16,
    SELECT_PLACE: 18,
    SELECT_POSITION: 19,
    SELECT_TRIBUTE: 20,
    SORT_CHAIN: 21,
    SELECT_COUNTER: 22,
    SELECT_SUM: 23,
    SELECT_DISFIELD: 24,
    SORT_CARD: 25,
    SELECT_UNSELECT_CARD: 26,
    CONFIRM_DECKTOP: 30,
    CONFIRM_CARDS: 31,
    SHUFFLE_DECK: 32,
    SHUFFLE_HAND: 33,
    REFRESH_DECK: 34,
    SWAP_GRAVE_DECK: 35,
    SHUFFLE_SET_CARD: 36,
    REVERSE_DECK: 37,
    DECK_TOP: 38,
    SHUFFLE_EXTRA: 39,
    NEW_TURN: 40,
    NEW_PHASE: 41,
    CONFIRM_EXTRATOP: 42,
    MOVE: 50,
    POS_CHANGE: 53,
    SET: 54,
    SWAP: 55,
    FIELD_DISABLED: 56,
    SUMMONING: 60,
    SUMMONED: 61,
    SPSUMMONING: 62,
    SPSUMMONED: 63,
    FLIPSUMMONING: 64,
    FLIPSUMMONED: 65,
    CHAINING: 70,
    CHAINED: 71,
    CHAIN_SOLVING: 72,
    CHAIN_SOLVED: 73,
    CHAIN_END: 74,
    CHAIN_NEGATED: 75,
    CHAIN_DISABLED: 76,
    CARD_SELECTED: 80,
    RANDOM_SELECTED: 81,
    BECOME_TARGET: 83,
    DRAW: 90,
    DAMAGE: 91,
    RECOVER: 92,
    EQUIP: 93,
    LPUPDATE: 94,
    CARD_TARGET: 96,
    CANCEL_TARGET: 97,
    PAY_LPCOST: 100,
    ADD_COUNTER: 101,
    REMOVE_COUNTER: 102,
    ATTACK: 110,
    BATTLE: 111,
    ATTACK_DISABLED: 112,
    DAMAGE_STEP_START: 113,
    DAMAGE_STEP_END: 114,
    MISSED_EFFECT: 120,
    BE_CHAIN_TARGET: 121,
    CREATE_RELATION: 122,
    RELEASE_RELATION: 123,
    TOSS_COIN: 130,
    TOSS_DICE: 131,
    ROCK_PAPER_SCISSORS: 132,
    HAND_RES: 133,
    ANNOUNCE_RACE: 140,
    ANNOUNCE_ATTRIB: 141,
    ANNOUNCE_CARD: 142,
    ANNOUNCE_NUMBER: 143,
    CARD_HINT: 160,
    TAG_SWAP: 161,
    RELOAD_FIELD: 162,
    AI_NAME: 163,
    SHOW_HINT: 164,
    PLAYER_HINT: 165,
    MATCH_KILL: 170,
    CUSTOM_MSG: 180,
    REMOVE_CARDS: 190,
    // ── turn-order (first/second) selection ──
    // The classic (pre-rewrite) ygopro had MSG_SELECT_TP = 4, but in THIS core (edo9300/ygopro-core
    // v11.0) opcode 4 is MSG_START and SELECT_TP is NOT a core duel message at all — first-player
    // selection is a host-framing concern (STOC_SELECT_TP, see network.ts) handled before OCG_StartDuel.
    // We define a synthetic opcode out of the real-opcode range so the decode/UI path is ready (driven
    // by mocks or a future host hook) without ever colliding with a message the core actually emits.
    SELECT_TP: 0xf0,
};
/** Reverse lookup for logging/debugging (e.g. msgName(50) === "MOVE"). */
const MSG_NAMES = Object.fromEntries(Object.entries(MSG).map(([k, v]) => [v, k]));
export const msgName = (code) => MSG_NAMES[code] ?? `MSG_UNKNOWN(${code})`;
/** Bit flags for OCG_DuelQuery — which card fields to include in a query response. */
export const QUERY = {
    CODE: 0x1,
    POSITION: 0x2,
    ALIAS: 0x4,
    TYPE: 0x8,
    LEVEL: 0x10,
    RANK: 0x20,
    ATTRIBUTE: 0x40,
    RACE: 0x80,
    ATTACK: 0x100,
    DEFENSE: 0x200,
    BASE_ATTACK: 0x400,
    BASE_DEFENSE: 0x800,
    REASON: 0x1000,
    REASON_CARD: 0x2000,
    EQUIP_CARD: 0x4000,
    TARGET_CARD: 0x8000,
    OVERLAY_CARD: 0x10000,
    COUNTERS: 0x20000,
    OWNER: 0x40000,
    STATUS: 0x80000,
    IS_PUBLIC: 0x100000,
    LSCALE: 0x200000,
    RSCALE: 0x400000,
    LINK: 0x800000,
    IS_HIDDEN: 0x1000000,
    COVER: 0x2000000,
    END: 0x80000000,
};
/** Card location bit flags (used in queries and placement). */
export const LOCATION = {
    DECK: 0x01,
    HAND: 0x02,
    MZONE: 0x04,
    SZONE: 0x08,
    GRAVE: 0x10,
    REMOVED: 0x20,
    EXTRA: 0x40,
    OVERLAY: 0x80,
    FZONE: 0x100,
    PZONE: 0x200,
};
/** Card position bit flags. FACEDOWN = the card's code must be hidden from non-controllers. */
export const POS = {
    FACEUP_ATTACK: 0x1,
    FACEDOWN_ATTACK: 0x2,
    FACEUP_DEFENSE: 0x4,
    FACEDOWN_DEFENSE: 0x8,
    FACEUP: 0x5,
    FACEDOWN: 0xa,
    ATTACK: 0x3,
    DEFENSE: 0xc,
};
/** Whether a position has any face-down bit set (code is hidden from the opponent). */
export const isFaceDown = (pos) => (pos & POS.FACEDOWN) !== 0;
/** Duel phase bit flags (MSG_NEW_PHASE payload is a u16 of one of these). */
export const PHASE = {
    DRAW: 0x01,
    STANDBY: 0x02,
    MAIN1: 0x04,
    BATTLE_START: 0x08,
    BATTLE_STEP: 0x10,
    DAMAGE: 0x20,
    DAMAGE_CAL: 0x40,
    BATTLE: 0x80,
    MAIN2: 0x100,
    END: 0x200,
};
const PHASE_NAMES = Object.fromEntries(Object.entries(PHASE).map(([k, v]) => [v, k]));
export const phaseName = (code) => PHASE_NAMES[code] ?? `PHASE_UNKNOWN(${code})`;
/** MSG_HINT hint_type values (common.h). */
export const HINT = {
    EVENT: 1,
    MESSAGE: 2,
    SELECTMSG: 3,
    OPSELECTED: 4,
    EFFECT: 5,
    RACE: 6,
    ATTRIB: 7,
    CODE: 8,
    NUMBER: 9,
    CARD: 10,
    ZONE: 11,
};
//# sourceMappingURL=messages.js.map